import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EnvironmentInjector,
  TemplateRef,
  ViewContainerRef,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { WooTabComponent } from './woo-tab/woo-tab.component';

@Component({
  selector: 'woo-tabs',
  imports: [CommonModule],
  templateUrl: './woo-tabs.component.html',
  styleUrl: './woo-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WooTabsComponent {
  readonly tabs = contentChildren(WooTabComponent);
  readonly componentHost = viewChild('componentHost', { read: ViewContainerRef });

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private envInjector = inject(EnvironmentInjector);

  name = input<string>('tab');
  default = input<string | undefined>();

  noSelection = input<string>('');
  noSelectionTemplate = input<TemplateRef<unknown> | null>(null);
  readonly queryParams = toSignal(this.route.queryParamMap, { initialValue: this.route.snapshot.queryParamMap });
  readonly activeTab = signal<WooTabComponent | null>(null);

  private initialLoad = true;

  readonly tabMap = computed(() =>
    this.tabs().reduce((map, tab) => {
      if (tab.name()) map[tab.name()] = tab;
      if (tab.name()) map[tab.name()] = tab;
      return map;
    }, {} as Record<string, WooTabComponent>),
  );

  constructor() {
    effect(() => {
      this.tabs();
      this.queryParams();
      this.tryActivateTabFromQueryParams();
    });
  }

  private tryActivateTabFromQueryParams() {
    const currentTabName = this.activeTab()?.name() ?? null;
    const param = this.route.snapshot.queryParamMap.get(this.name());

    if (param !== null && param === currentTabName) {
      return;
    }

    let match: WooTabComponent | null = null;

    if (param) {
      match = this.tabMap()[param] ?? null;
    } else if (this.initialLoad && this.default()) {
      match = this.tabMap()[this.default()!] ?? null;
      this.selectTab(match, true);

      this.initialLoad = false;
      return;
    }

    this.initialLoad = false;

    this.selectTab(match, false);
  }

  private get canShowEmptyView(): boolean {
    return !!(this.noSelectionTemplate() || this.noSelection()?.trim().length);
  }

  selectTab(tab: WooTabComponent | null, updateQuery = true): void {
    const host = this.componentHost();
    const current = this.activeTab();

    if (tab === current) {
      if (!this.canShowEmptyView || !host) return;

      this.activeTab.set(null);
      host.clear();

      if (updateQuery) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { [this.name()]: null },
          queryParamsHandling: 'merge',
        });
      }
      return;
    }

    this.activeTab.set(tab);
    this.renderComponentIfNeeded(tab);

    if (updateQuery && tab?.name()) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { [this.name()]: tab.name() },
        queryParamsHandling: 'merge',
      });
    }
  }

  private renderComponentIfNeeded(tab: WooTabComponent | null) {
    const host = this.componentHost();
    if (!host) return;
    host.clear();

    if (tab?.template()) host.createEmbeddedView(tab.template());
    if (tab?.component())
      host.createComponent(tab.component(), {
        environmentInjector: this.envInjector,
      });
  }
}
